import { IShareContact } from '@app/models/ShareContact'
import {
  actionCreators,
  IGlobalContactEmailAddressesState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import * as React from 'react'
import AutoSuggest from 'react-autosuggest'

interface IContactEmailAddressSelectorProps {
  className?: string
  disabled?: boolean
  name?: string
  onSelected: (selectedValue: any) => void
  placeholder?: string
  required?: boolean
  style?: React.CSSProperties
}

type ContactEmailAddressSelectorProps = IContactEmailAddressSelectorProps &
  IGlobalContactEmailAddressesState &
  typeof actionCreators

interface IContactEmailAddressSelectorState {
  filter: string
  suggestions: IShareContact[]
}

export class ContactEmailAddressSelector extends React.PureComponent<
  ContactEmailAddressSelectorProps,
  IContactEmailAddressSelectorState
> {
  constructor(props: ContactEmailAddressSelectorProps) {
    super(props)
    this.state = {
      filter: '',
      suggestions: [],
    }
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.getContactEmailAddresses()
  }

  public render() {
    const theme: any = {
      container: {
        position: 'relative',
      },
      input: {},
      inputFocused: {
        outline: 'none',
      },
      inputOpen: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
      suggestion: {
        cursor: 'pointer',
        padding: '10px 20px',
      },
      suggestionHighlighted: {
        backgroundColor: '#ddd',
      },
      suggestionsContainer: {
        display: 'none',
      },
      suggestionsContainerOpen: {
        backgroundColor: '#fff',
        border: '1px solid #aaa',
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
        display: 'block',
        fontFamily: 'Helvetica, sans-serif',
        fontSize: 16,
        fontWeight: 300,
        position: 'absolute',
        top: 51,
        width: '100%',
        zIndex: 2,
      },
      suggestionsList: {
        listStyleType: 'none',
        margin: 0,
        padding: 0,
      },
    }

    const inputProps = {
      className: 'form-control',
      disabled: this.props.disabled,
      name: this.props.name,
      onChange: this._onChange,
      placeholder: this.props.placeholder,
      required: this.props.required,
      value: this.state.filter,
    }

    return (
      <div className={this.props.className} style={this.props.style}>
        <AutoSuggest
          getSuggestionValue={this._getSuggestionValue}
          highlightFirstSuggestion
          inputProps={inputProps}
          onSuggestionSelected={this._onSuggestionSelected}
          onSuggestionsClearRequested={this._onSuggestionsClearRequested}
          onSuggestionsFetchRequested={this._onSuggestionsFetchRequested}
          renderSuggestion={this._renderSuggestion}
          suggestions={this.state.suggestions}
          theme={theme}
        />
      </div>
    )
  }

  private _getSuggestions(filterValue: string): IShareContact[] {
    if (!filterValue || this.props.contactEmailAddresses.contacts.size === 0) {
      return []
    }

    const inputValue = filterValue.trim().toLowerCase()

    if (!inputValue) {
      return []
    }

    const filteredByName = this.props.contactEmailAddresses.contacts
      .toArray()
      .filter((c) => c.name.toLowerCase().indexOf(inputValue) >= 0)
      .slice(0, 10)

    const filteredByEmail = this.props.contactEmailAddresses.contacts
      .toArray()
      .filter(
        (c) =>
          c.addresses.size > 0 &&
          c.addresses.find(
            (item: any) => item.address.toLowerCase().indexOf(inputValue) >= 0,
          ),
      )
      .slice(0, 10)

    return filteredByName.concat(filteredByEmail)
  }

  private _getSuggestionValue(suggestion: IShareContact) {
    return `${suggestion.name}`
  }

  private _onChange(
    event: React.FormEvent<any>,
    params?: AutoSuggest.ChangeEvent,
  ) {
    if (params) {
      this.setState({ filter: params.newValue })
    }
  }

  private _onSuggestionsFetchRequested(
    request: AutoSuggest.SuggestionsFetchRequestedParams,
  ) {
    this.setState({ suggestions: this._getSuggestions(request.value) })
  }

  private _onSuggestionsClearRequested() {
    this.setState({ suggestions: [] })
  }

  private _onSuggestionSelected(
    event: React.FormEvent<any>,
    data: AutoSuggest.SuggestionSelectedEventData<IShareContact>,
  ) {
    this.props.onSelected(data.suggestion as any)
    this.setState({
      filter: '',
      suggestions: [],
    })
  }

  private _renderSuggestion(suggestion: IShareContact) {
    if (suggestion.type === 'Contact') {
      return (
        <div>
          <div>{suggestion.name}</div>
          <div className='small text-muted'>
            {suggestion.addresses.first().address}
          </div>
        </div>
      )
    } else {
      return (
        <div>
          <div>{`${suggestion.name} (${
            suggestion.addresses.size
          } contacts)`}</div>
        </div>
      )
    }
  }
}
